/**
 * Window controls that degrade in a plain browser.
 *
 * The dev server runs outside Tauri, so every call here is guarded and the
 * Tauri API is imported dynamically — importing it eagerly would pull the IPC
 * bridge into the browser bundle path and throw before the shell renders.
 */

export const isTauri = (): boolean =>
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

async function currentWindow() {
  if (!isTauri()) return null;
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  return getCurrentWindow();
}

export async function minimizeWindow(): Promise<void> {
  (await currentWindow())?.minimize();
}

export async function toggleMaximizeWindow(): Promise<void> {
  (await currentWindow())?.toggleMaximize();
}

export async function closeWindow(): Promise<void> {
  const w = await currentWindow();
  if (w) await w.close();
  else console.warn("[window] close is a no-op outside Tauri");
}

/** Tauri fullscreen when available, the browser Fullscreen API otherwise. */
export async function toggleFullScreen(): Promise<void> {
  const w = await currentWindow();
  if (w) {
    const full = await w.isFullscreen();
    await w.setFullscreen(!full);
    return;
  }
  if (document.fullscreenElement) await document.exitFullscreen();
  else await document.documentElement.requestFullscreen().catch(() => undefined);
}
